/* SWP Calculator Logic — Systematic Withdrawal Plan Math, Charts & CSV Export */

let swpDonutChart = null;
let swpGrowthChart = null;

function getSwpInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    corpus: getVal('swpCorpus', 5000000),
    withdrawal: getVal('swpWithdrawal', 30000),
    returnRate: getVal('swpReturn', 10),
    years: getVal('swpYears', 20),
    inflation: getVal('swpInflation', 6),
  };
}

function updateSwpUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('corpus', params.corpus);
    url.searchParams.set('withdrawal', params.withdrawal);
    url.searchParams.set('returnRate', params.returnRate);
    url.searchParams.set('years', params.years);
    url.searchParams.set('inflation', params.inflation);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseSwpUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const corpus = searchParams.get('corpus');
  const withdrawal = searchParams.get('withdrawal');
  const returnRate = searchParams.get('returnRate');
  const years = searchParams.get('years');
  const inflation = searchParams.get('inflation');

  return {
    corpus: corpus !== null ? +corpus : null,
    withdrawal: withdrawal !== null ? +withdrawal : null,
    returnRate: returnRate !== null ? +returnRate : null,
    years: years !== null ? +years : null,
    inflation: inflation !== null ? +inflation : null,
  };
}

function calculateSwp(params) {
  const { corpus, withdrawal, returnRate, years, inflation } = params;

  const rMonthly = returnRate / 12 / 100;
  const inflDecimal = inflation / 100;

  let balance = corpus;
  let totalWithdrawn = 0;
  let totalInterest = 0;
  let depletedMonth = null;

  const schedule = [];

  for (let yr = 1; yr <= years; yr++) {
    const openingBalance = balance;
    let annualWithdrawal = 0;
    let annualInterest = 0;

    const currentMonthlyWithdrawal = withdrawal * Math.pow(1 + inflDecimal, yr - 1);

    for (let m = 1; m <= 12; m++) {
      if (balance <= 0) {
        if (depletedMonth === null) {
          depletedMonth = (yr - 1) * 12 + m - 1;
        }
        break;
      }

      const interest = balance * rMonthly;
      const payout = Math.min(balance + interest, currentMonthlyWithdrawal);

      balance = balance + interest - payout;
      annualInterest += interest;
      annualWithdrawal += payout;
      totalWithdrawn += payout;
      totalInterest += interest;
    }

    schedule.push({
      year: yr,
      openingBalance: Math.round(openingBalance),
      annualPayout: Math.round(annualWithdrawal),
      interestEarned: Math.round(annualInterest),
      closingBalance: Math.max(0, Math.round(balance)),
      monthlyPayout: Math.round(currentMonthlyWithdrawal),
    });

    if (balance <= 0 && depletedMonth === null) {
      depletedMonth = yr * 12;
    }
  }

  return {
    initialCorpus: Math.round(corpus),
    totalWithdrawn: Math.round(totalWithdrawn),
    totalInterest: Math.round(totalInterest),
    finalCorpus: Math.max(0, Math.round(balance)),
    depletedMonth,
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('swpCorpusDisplay', fmt(params.corpus));
  setTxt('swpWithdrawalDisplay', fmt(params.withdrawal));
  setTxt('swpReturnDisplay', `${params.returnRate}%`);
  setTxt('swpYearsDisplay', `${params.years} yrs`);
  setTxt('swpInflationDisplay', `${params.inflation}%/yr`);
}

function renderSwpResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  const isDepleted = result.finalCorpus === 0;

  if (isDepleted && result.depletedMonth) {
    const depYears = Math.floor(result.depletedMonth / 12);
    const depMonths = result.depletedMonth % 12;
    const timeStr = depYears > 0 
      ? `${depYears} yrs ${depMonths > 0 ? depMonths + ' mos' : ''}`.trim()
      : `${depMonths} months`;

    setTxt('swpVerdictHeadline', `Corpus Depleted in ${timeStr}`);
    setTxt('swpVerdictDetail', `Your initial corpus of ${fmt(result.initialCorpus)} was fully exhausted after withdrawing ${fmt(result.totalWithdrawn)} in total income.`);
  } else {
    setTxt('swpVerdictHeadline', `Total Payout: ${fmt(result.totalWithdrawn)}`);
    setTxt('swpVerdictDetail', `Your corpus of ${fmt(result.initialCorpus)} generated ${fmt(result.totalWithdrawn)} in withdrawals and still leaves ${fmt(result.finalCorpus)} remaining after ${params.years} years.`);
  }

  setTxt('swpTotalWithdrawnValue', fmt(result.totalWithdrawn));
  setTxt('swpFinalCorpusValue', fmt(result.finalCorpus));
  setTxt('swpInitialCorpusValue', fmt(result.initialCorpus));
  setTxt('swpTotalInterestValue', fmt(result.totalInterest));
  setTxt('swpNetPortfolioValue', fmt(result.finalCorpus));

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.initialCorpus, result.totalInterest, result.totalWithdrawn);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('SWP Chart rendering error:', err);
  }
}

function renderDonutChart(initial, interest, withdrawn) {
  const ctx = document.getElementById('swpDonutChart');
  if (!ctx) return;

  if (swpDonutChart) swpDonutChart.destroy();

  swpDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Total Payout Withdrawn', 'Total Interest Earned', 'Remaining Balance'],
      datasets: [
        {
          data: [withdrawn, interest, Math.max(0, initial + interest - withdrawn)],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent'), '#3B82F6'],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('swpGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const balanceData = schedule.map((r) => r.closingBalance);
  let cumWithdrawn = 0;
  const withdrawnData = schedule.map((r) => {
    cumWithdrawn += r.annualPayout;
    return cumWithdrawn;
  });

  if (swpGrowthChart) swpGrowthChart.destroy();

  swpGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Remaining Corpus Balance',
          data: balanceData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2.5,
        },
        {
          label: 'Cumulative Withdrawals',
          data: withdrawnData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('swpScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>Year ${row.year}</td>
      <td>${fmt(row.openingBalance)}</td>
      <td class="advantage-buyer">-${fmt(row.annualPayout)}</td>
      <td class="advantage-renter">+${fmt(row.interestEarned)}</td>
      <td style="font-weight: 600;">${fmt(row.closingBalance)}</td>
    </tr>
  `).join('');
}

function exportSwpCsv() {
  const params = getSwpInputs();
  const result = calculateSwp(params);
  const headers = ['Year', 'Opening Balance', 'Annual Payout', 'Interest Earned', 'Closing Balance'];
  const rows = result.schedule.map((r) => [
    r.year, r.openingBalance, r.annualPayout, r.interestEarned, r.closingBalance
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `swp-schedule-${params.years}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applySwpCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultCorpus = 5000000;
  let defaultWithdrawal = 30000;
  let maxCorpus = 100000000;
  let maxWithdrawal = 1000000;
  let stepCorpus = 50000;
  let stepWithdrawal = 1000;

  if (curr === 'INR') {
    defaultCorpus = 5000000;
    defaultWithdrawal = 30000;
    maxCorpus = 100000000;
    maxWithdrawal = 500000;
    stepCorpus = 100000;
    stepWithdrawal = 1000;
  } else if (curr === 'JPY') {
    defaultCorpus = 50000000;
    defaultWithdrawal = 300000;
    maxCorpus = 1000000000;
    maxWithdrawal = 5000000;
    stepCorpus = 1000000;
    stepWithdrawal = 10000;
  }

  const cSlider = document.getElementById('swpCorpus');
  if (cSlider) {
    cSlider.min = 500000;
    cSlider.max = maxCorpus;
    cSlider.step = stepCorpus;
    cSlider.value = overrideParams.corpus ?? defaultCorpus;
  }

  const wSlider = document.getElementById('swpWithdrawal');
  if (wSlider) {
    wSlider.max = maxWithdrawal;
    wSlider.step = stepWithdrawal;
    wSlider.value = overrideParams.withdrawal ?? defaultWithdrawal;
  }

  const rSlider = document.getElementById('swpReturn');
  if (rSlider && overrideParams.returnRate !== null) {
    rSlider.value = overrideParams.returnRate;
  }

  const ySlider = document.getElementById('swpYears');
  if (ySlider && overrideParams.years !== null) {
    ySlider.value = overrideParams.years;
  }

  const iSlider = document.getElementById('swpInflation');
  if (iSlider && overrideParams.inflation !== null) {
    iSlider.value = overrideParams.inflation;
  }
}

function recalculateSwp() {
  const params = getSwpInputs();
  updateDisplays(params);
  updateSwpUrlParams(params);
  const result = calculateSwp(params);
  renderSwpResults(result, params);
}

function initSwpCalculator() {
  if (!document.getElementById('swpCorpus')) return;

  const urlParams = parseSwpUrlParams();

  initCountry((country) => {
    applySwpCountryRanges(country, urlParams);
    recalculateSwp();
  });

  const inputs = ['swpCorpus', 'swpWithdrawal', 'swpReturn', 'swpYears', 'swpInflation'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateSwp);
      el.addEventListener('change', recalculateSwp);
    }
  });

  const exportBtn = document.getElementById('exportSwpCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportSwpCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initSwpCalculator();
});
